import React from 'react';
import { BrainGraph } from '../../components/deck/BrainGraph';
import { Slide } from '../../components/deck/Slide';
import { Caption, Eyebrow, INK, Label, Reveal, Title } from '../../components/deck/Type';
const SERIF = {
  fontFamily: 'var(--font-serif)'
} as const;
type Market = {
  name: string;
  flows: string[];
  wedge?: boolean;
};
const left: Market[] = [{
  name: 'Venture Capital',
  flows: ['Startup diligence', 'Investment memos', 'Portfolio intelligence'],
  wedge: true
}, {
  name: 'Corporate Innovation',
  flows: ['Technology scouting', 'Vendor assessment', 'Partnership diligence']
}, {
  name: 'M&A',
  flows: ['Target screening', 'Technology diligence', 'Market intelligence']
}, {
  name: 'Procurement',
  flows: ['Vendor diligence', 'Technology comparison', 'Risk assessment']
}];
const right: Market[] = [{
  name: 'Research Labs',
  flows: ['Technology assessment', 'Commercialization', 'Research intelligence']
}, {
  name: 'Technology Diligence',
  flows: ['Technology classification', 'Research assessment', 'Documentation intelligence']
}, {
  name: 'Private Equity',
  flows: ['Technology diligence', 'Market diligence', 'Portfolio intelligence']
}, {
  name: 'Government Innovation',
  flows: ['Grant assessment', 'Technology evaluation', 'Innovation intelligence']
}];
function MarketItem({
  market,
  side,
  i
}: {
  market: Market;
  side: 'left' | 'right';
  i: number;
}) {
  const line = <span className="h-px flex-1" style={{
    background: market.wedge ? INK.blue : 'rgba(255,255,255,0.14)'
  }} aria-hidden data-id="element-351" data-mp="6rq1qvi3qo2r" />;
  return <Reveal delay={0.05 * i} className="flex items-center gap-[1.2cqw]" data-id="element-352" data-mp="bl2rj8yip0x3">
      {side === 'right' && line}
      <div className={side === 'left' ? 'w-[24cqw] text-right' : 'w-[24cqw]'} data-id="element-353" data-mp="3tv4nb9svc8ud">
        {market.wedge && <div className="mb-[1cqh]" data-id="element-354" data-mp="3jyrs27hodmxi">
            <Label tone={INK.blue} data-id="element-355" data-mp="3jzt70n1tatki">Initial wedge · in market</Label>
          </div>}
        <div data-id="element-356" data-mp="3oy4ggwo73227">
          <p className="text-[1.45cqw] leading-none" style={{
          ...SERIF,
          color: market.wedge ? '#F4F7FC' : '#C3CBD9'
        }} data-id="element-357" data-mp="3owjlj5xyxh3t">
            {market.name}
          </p>
        </div>
        <p className="mt-[1cqh] text-[0.86cqw]" style={{
        color: market.wedge ? '#AEC9F5' : '#7C8699'
      }} data-id="element-358" data-mp="310yelyv6sg1a">
          {market.flows.join(' · ')}
        </p>
      </div>
      {side === 'left' && line}
    </Reveal>;
}
export function MarketsSlide() {
  return <Slide data-id="element-359" data-mp="3osncduqs1wpm">
      <div className="flex items-start justify-between gap-[3cqw]" data-id="element-360" data-mp="3osdlm4xwrliv">
        <div className="max-w-[62%]" data-id="element-361" data-mp="3jt0wtenh84dn">
          <Eyebrow data-id="element-362" data-mp="3ju2bru7m5b0n">Expansion</Eyebrow>
          <Title className="mt-[2.2cqh]" data-id="element-363" data-mp="3jqxwa0op6suo">One intelligence engine. Multiple high-value markets.</Title>
        </div>
        <Caption className="max-w-[28%] text-right" data-id="element-364" data-mp="39uuyt84sjie2">
          One architecture serving many professional workflows — not eight separate products.
        </Caption>
      </div>

      <div className="mt-[3cqh] grid min-h-0 flex-1 grid-cols-[1fr_20cqw_1fr] items-center" data-id="element-365" data-mp="23co9vc40ne6h">
        <div className="flex h-full flex-col justify-around" data-id="element-366" data-mp="1yed174pgj9pu">
          {left.map((m, i) => <MarketItem key={m.name} market={m} side="left" i={i} data-id="element-367" data-mp="dzglknod3yx6" data-mpi={i} />)}
        </div>

        <div className="relative aspect-square w-full rounded-full border border-[#3B8CFF]/45 bg-[#3B8CFF]/[0.06]" data-id="element-368" data-mp="ix9sxk1eiyz1">
          <div className="absolute inset-[10%] opacity-60" data-id="element-369" data-mp="ntqdy5db0x7w">
            <BrainGraph density={1} className="h-full w-full" data-id="element-370" data-mp="snl33oboi4ed" />
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center" data-id="element-371" data-mp="xm5y99c2pxvy">
            <p className="bg-[#070B14]/70 px-[0.6cqw] text-[1.7cqw] leading-none text-[#F4F7FC]" style={SERIF} data-id="element-372" data-mp="11momkterclte">
              DiligenceGPT™
            </p>
            <span className="mt-[1.2cqh] bg-[#070B14]/70 px-[0.6cqw] text-[0.7cqw] uppercase tracking-[0.22em] text-[#3B8CFF]" data-id="element-373" data-mp="16k80ttbggylc">
              One intelligence architecture
            </span>
          </div>
        </div>

        <div className="flex h-full flex-col justify-around" data-id="element-374" data-mp="3dcr2qse49fwq">
          {right.map((m, i) => <MarketItem key={m.name} market={m} side="right" i={i + 4} data-id="element-375" data-mp="17ohxtddr77c9" data-mpi={i} />)}
        </div>
      </div>

      <Reveal delay={0.5} className="mt-[2cqh] flex items-center justify-between gap-[3cqw] border-t border-white/12 pt-[2.2cqh]" data-id="element-376" data-mp="12pnfecjp7kso">
        <p className="text-[1.45cqw] text-[#F4F7FC]" style={SERIF} data-id="element-377" data-mp="1gnva0dt9ia0o">
          VC is the wedge. Technology intelligence is the platform opportunity.
        </p>
        <Caption className="max-w-[44%] text-right" data-id="element-378" data-mp="oxqtwrcjc959">
          Same data, models, memory and workflows. New markets add templates and sources, not a new platform.
        </Caption>
      </Reveal>
    </Slide>;
}